import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-article-line text-2xl text-foreground-500"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 font-heading mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-600 mb-6">该文章可能已被移除或链接有误</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '文章', path: '/' },
    { label: article.title },
  ];

  const relatedArticles = articles
    .filter((a) => a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: article.thumbnail,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.date,
            publisher: {
              '@type': 'Organization',
              name: '在线观看',
            },
          }),
        }}
      />
      <Navbar />
      <main>
        <article>
          <div className="pt-20 pb-8 bg-background-100/50 border-b border-background-200/60">
            <div className="max-w-[900px] mx-auto px-4 md:px-6">
              <Breadcrumb items={breadcrumbItems} />
              <div className="mt-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xs px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 font-medium">
                    {article.category}
                  </span>
                  <span className="text-xs text-foreground-600">{article.readTime}</span>
                </div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 font-heading leading-tight">
                  {article.title}
                </h1>
                <div className="flex items-center gap-3 mt-4">
                  <span className="text-sm text-foreground-600">{article.author}</span>
                  <span className="text-foreground-500">·</span>
                  <span className="text-sm text-foreground-600">{article.date}</span>
                </div>
                <div className="flex items-center gap-2 mt-4 flex-wrap">
                  {article.tags.map((tag) => (
                    <span key={tag} className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="py-10">
            <div className="max-w-[900px] mx-auto px-4 md:px-6">
              <div className="relative overflow-hidden rounded-lg aspect-[2/1] bg-background-200 mb-10">
                <img
                  src={article.thumbnail}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose prose-invert max-w-none
                  prose-headings:text-foreground-950 prose-headings:font-heading
                  prose-h3:text-xl prose-h3:font-bold prose-h3:mt-8 prose-h3:mb-4
                  prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5 prose-p:text-base
                  prose-strong:text-foreground-950
                  text-foreground-700 text-base leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </div>
          </div>
        </article>

        <section className="py-12 bg-background-100/50">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <h3 className="text-lg font-bold text-foreground-950 font-heading mb-6">相关文章</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/article/${related.id}`}
                  className="group cursor-pointer p-4 rounded-lg bg-background-100 border border-background-200/60 hover:border-background-300/60 transition-all duration-300"
                >
                  <div className="relative overflow-hidden rounded-lg aspect-[16/9] bg-background-200 mb-3">
                    <img
                      src={related.thumbnail}
                      alt={related.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                    />
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2">
                    {related.title}
                  </h4>
                  <p className="text-xs text-foreground-600 mt-1 line-clamp-2">{related.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}